// caiet UC91-02 — "unul sau mai multe coduri de medicamente", picked through the
// extended search of the combined nomenclator. What travels out is the
// nomenclator code: core.stock.medicine_code holds the code, so a selection
// carrying the catalog id charts a flat zero line. There is no cap on the
// selection; the caiet sets none.
import { useEffect, useRef, useState, type ElementRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudChip, MudTextInput, MudTag } from '@egov-moldova/mud-react';
import { formatDate, useAnchoredOverlay, useComboboxA11y } from '@simsm/ui-shared';
import { formatMedicineLabel, splitMedicineLabel } from '@simsm/shared-types';
import {
  describeAuthorization,
  searchMedicines,
  todayIso,
  type MedicineHit,
} from '../../features/reporting/medicine-api';

export interface SelectedMedicine {
  code: string;
  label: string;
}

interface MedicineMultiSelectProps {
  selected: SelectedMedicine[];
  colorFor: (code: string) => string;
  onAdd: (medicine: SelectedMedicine) => void;
  onRemove: (code: string) => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function MedicineMultiSelect({
  selected,
  colorFor,
  onAdd,
  onRemove,
}: MedicineMultiSelectProps): JSX.Element {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Escape hides the suggestions without discarding the query — see the same
  // pair in MedicineCombobox.
  const [dismissed, setDismissed] = useState(false);
  const debounced = useDebounced(query, 200);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<ElementRef<typeof MudTextInput>>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // The helper's `open` must be the condition the panel actually renders under:
  // it measures the portalled element, and an element that is not mounted cannot
  // be measured.
  const panelOpen = open && !dismissed && debounced.trim().length >= 2;
  // The panel is portalled to <body>, so placement belongs to the shared
  // anchored-overlay helper and not to this component: it flips above the field
  // when the row sits near the fold, clamps to every viewport edge and
  // repositions while the page scrolls — spec 002 contracts/shared-fixes.md §2.
  // Computing a `position: fixed` offset here again is the defect returning.
  //
  // The height cap stays this component's own. `.simsm-medmulti__panel`'s
  // `max-height: 320px` in global.css is what the panel renders at: the helper
  // reads that rule, places the panel as the box it makes, and adds a cap of its
  // own only when even 320px does not fit beside the trigger. It has to work that
  // way round — an inline `max-height` outranks any class rule, so writing one
  // unconditionally repeals the consumer's, which is how this panel came to render
  // 344px tall, uncapped and unscrollable, under an inline cap of 601px.
  const { style: panelStyle, placement } = useAnchoredOverlay({
    open: panelOpen,
    triggerRef: rootRef,
    overlayRef: panelRef,
    matchTriggerWidth: true,
  });

  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const search = useQuery({
    queryKey: ['uc91-medicine-search', debounced],
    queryFn: () => searchMedicines(debounced),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  // Read once per render, so every row in one panel judges its expiry against the
  // same day.
  const today = todayIso();
  const selectedCodes = new Set(selected.map((medicine) => medicine.code));
  const hits = (search.data?.items ?? []).filter((hit) => !selectedCodes.has(hit.code));

  function handlePick(hit: MedicineHit): void {
    onAdd({ code: hit.code, label: formatMedicineLabel(hit) });
    setQuery('');
    setOpen(false);
  }

  // Audit E7 / WCAG SC 2.1.1: the field was a bare `mud-text-input` beside a
  // `role="group"` panel of buttons — no combobox role, no arrow keys, no
  // Escape, and the portalled panel sitting after the whole page so the first
  // suggestion was 24 Tab presses away. See `combobox-a11y.ts` for why this is
  // the focus-moves-into-the-listbox variant of the ARIA pattern.
  const combobox = useComboboxA11y({
    open: panelOpen,
    optionCount: hits.length,
    fieldRef,
    listboxRef,
    onSelect: (index) => {
      const hit = hits[index];
      if (hit) handlePick(hit);
    },
    onOpen: () => {
      setOpen(true);
      setDismissed(false);
    },
    onClose: () => setDismissed(true),
    onTypeahead: (character) => {
      setQuery(query + character);
      setOpen(true);
      setDismissed(false);
    },
  });

  return (
    <div
      ref={rootRef}
      className="simsm-medmulti"
      // The field's keys are caught here rather than on `mud-text-input`: keydown is
      // composed, so it crosses the shadow boundary and reaches this wrapper
      // whichever element inside the component is focused.
      onKeyDown={combobox.onFieldKeyDown}
    >
      {/* The wireframe's `ichip cu-avatar` (Stocuri.tsx:98) is AGE's input chip
          with the series colour in its leading slot. The words go in the default
          slot: mud-chip's `label` prop renders no visible text.
          The chip is named by the full label and MUD adds the verb to its ×, so the
          button reads "Elimină <label>" once, in the page language. */}
      <ul className="simsm-medmulti__chips">
        {selected.map((medicine) => (
          <li key={medicine.code}>
            <MudChip
              type="input"
              size="md"
              removable
              aria-label={medicine.label}
              onMudRemove={() => onRemove(medicine.code)}
            >
              <span
                slot="icon-start"
                className="simsm-medmulti__swatch"
                style={{ backgroundColor: colorFor(medicine.code) }}
                aria-hidden="true"
              />
              {/* The wireframe's chip is `denumire · doza` (Stocuri.tsx:98), not the
                  whole identity: several chips share one row and the swatch, not
                  the words, is what ties one to its line. The dose it also shows
                  cannot be recovered here — the chip is rebuilt from the single
                  string the `labels` URL parameter carries — so the chip takes the
                  leading name and the full label stays in its accessible name. */}
              {splitMedicineLabel(medicine.label).name}
            </MudChip>
          </li>
        ))}
      </ul>

      <MudTextInput
        ref={fieldRef}
        size="md"
        value={query}
        aria-label={t('stockChart.medicine.search_placeholder')}
        placeholder={t('stockChart.medicine.search_placeholder')}
        onMudInput={(e: CustomEvent<{ value: string }>) => {
          setQuery(e.detail.value);
          setOpen(true);
          setDismissed(false);
        }}
        onMudFocus={() => {
          if (!combobox.shouldOpenOnFieldFocus()) return;
          setOpen(true);
          setDismissed(false);
        }}
      />

      {panelOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="simsm-medmulti__panel"
              style={panelStyle}
              // Asserted by the e2e geometry check, and a flipped panel may want
              // different chrome later.
              data-placement={placement ?? undefined}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {search.isFetching ? (
                <div className="simsm-medmulti__row simsm-medmulti__row--meta" role="status">
                  {t('stockChart.medicine.loading')}
                </div>
              ) : null}
              {!search.isFetching && search.isError ? (
                // A search that failed is not a search that found nothing. This
                // row used to say "no results for «…»" whenever the request was
                // refused, so an unreachable catalogue read as a medicine that
                // does not exist — on the screen where a stock report is filled in.
                <div className="simsm-medmulti__row simsm-medmulti__row--meta" role="status">
                  {t('common.load_error')}
                </div>
              ) : null}
              {!search.isFetching && !search.isError && hits.length === 0 ? (
                <div className="simsm-medmulti__row simsm-medmulti__row--meta" role="status">
                  {t('stockChart.medicine.no_results', { query: debounced })}
                </div>
              ) : null}
              {/* The listbox exists only while it holds options: an empty one is a
                  role whose required children are missing, and the three messages
                  above are status text, not choices. */}
              {hits.length > 0 ? (
                <div
                  ref={listboxRef}
                  id={combobox.listboxId}
                  role="listbox"
                  // In the tab order, and named, because the panel around it is a
                  // scroll container: a scrollable region whose only contents are
                  // `tabindex="-1"` options is unreachable by keyboard in Safari,
                  // and axe reports it as such (`scrollable-region-focusable`). The
                  // options keep roving focus; this is the container that answers
                  // for them, and arrow keys and Escape work on it.
                  tabIndex={0}
                  className="simsm-medmulti__list"
                  aria-label={t('stockChart.medicine.label')}
                  onKeyDown={combobox.onListboxKeyDown}
                >
                  {hits.map((hit, index) => {
                    // caiet UC91-02 (TC-UC91-02-M01): the result carries the authorization
                    // expiry for the authorized codes, as the other searches of the
                    // nomenclator do (MedicineCombobox).
                    const authorization = describeAuthorization(hit, today);
                    return (
                      <div
                        key={hit.code}
                        id={combobox.optionId(index)}
                        role="option"
                        tabIndex={-1}
                        aria-selected={combobox.activeIndex === index}
                        className="simsm-medmulti__row"
                        onFocus={() => combobox.onOptionFocus(index)}
                        onClick={() => combobox.selectOption(index)}
                      >
                        <span className="simsm-medmulti__title">{formatMedicineLabel(hit)}</span>
                        <span className="simsm-medmulti__meta">
                          <span className="simsm-medmulti__code">{hit.code}</span>
                          {hit.atcCode ? ` · ${hit.atcCode}` : null}
                          {hit.authorized ? null : (
                            <MudTag
                              variant="info"
                              semantic="muted"
                              type="subtle"
                              size="sm"
                              label={t('medicine_combobox.unauthorized')}
                            />
                          )}
                        </span>
                        {authorization ? (
                          <span
                            className={`simsm-medicombo__authorization${
                              authorization.expired
                                ? ' simsm-medicombo__authorization--expired'
                                : ''
                            }`}
                          >
                            {t(authorization.key, { date: formatDate(authorization.iso) })}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
